/**
 * UCT2 timeline chunk decoder (pure: no DOM, works in a worker and in node).
 *
 * Byte layout and per-encoding pseudo-code: src/web_export/schema/README.md.
 * Reference decoder: src/web_export/common.py `unpack_chunk` / `decode_block`.
 *
 * Blocks are car-major; the decoder returns chunk-local **frame-major** arrays
 * (`a[k * nCars + car]`, k = frame within the chunk) in the decoded dtype, ready to be copied into the
 * full-race arrays at `f0 * nCars`.
 */

export const UCT2_MAGIC = 'UCT2';
export const UCT2_VERSION = 2;
export const HEADER_BYTES = 64;
export const FIELD_ENTRY_BYTES = 20;
export const BLOCK_ALIGN = 8;

export const ENC_RAW = 1;
export const ENC_INT_DELTA = 2;
export const ENC_F32_DELTA = 3;

/** UCT2 dtype codes (decoded value types). */
export const DTYPE = { u8: 1, i8: 2, u16: 3, i16: 4, u32: 5, i32: 6, f32: 7, f64: 8 } as const;
export type DtypeCode = (typeof DTYPE)[keyof typeof DTYPE];

export type DecodedArray =
  | Uint8Array
  | Int8Array
  | Uint16Array
  | Int16Array
  | Uint32Array
  | Int32Array
  | Float32Array
  | Float64Array;

export interface ChunkHeader {
  magic: string;
  version: number;
  f0: number;
  n: number;
  nCars: number;
  nFields: number;
  paddingZero: boolean;
}

export interface FieldEntry {
  id: number;
  enc: number;
  dtype: DtypeCode;
  order: number;
  /** f32 copy of the scale stored in the chunk (decode with the exact manifest f64 scale instead) */
  scale: number;
  offset: number;
  length: number;
  wrap: number;
}

export interface DecodedChunk {
  header: ChunkHeader;
  table: FieldEntry[];
  /** field id → chunk-local frame-major array (n × nCars) */
  fields: Map<number, DecodedArray>;
}

/** What the caller expects (from manifest.timeline.files[i]); every given key is checked. */
export interface ChunkExpect {
  f0?: number;
  n?: number;
  nCars?: number;
  bytes?: number;
}

export class ChunkError extends Error {
  override name = 'ChunkError';
}

function isDtypeCode(c: number): c is DtypeCode {
  return Number.isInteger(c) && c >= 1 && c <= 8;
}

export function parseHeader(buf: ArrayBuffer): ChunkHeader {
  if (buf.byteLength < HEADER_BYTES) throw new ChunkError(`chunk too short: ${String(buf.byteLength)} bytes`);
  const dv = new DataView(buf);
  const magic = String.fromCharCode(dv.getUint8(0), dv.getUint8(1), dv.getUint8(2), dv.getUint8(3));
  let paddingZero = true;
  for (let o = 14; o < HEADER_BYTES; o++) if (dv.getUint8(o) !== 0) paddingZero = false;
  return {
    magic,
    version: dv.getUint16(4, true),
    f0: dv.getUint32(6, true),
    n: dv.getUint16(10, true),
    nCars: dv.getUint8(12),
    nFields: dv.getUint8(13),
    paddingZero,
  };
}

export function parseTable(buf: ArrayBuffer, h: ChunkHeader): FieldEntry[] {
  const dv = new DataView(buf);
  const end = HEADER_BYTES + h.nFields * FIELD_ENTRY_BYTES;
  if (end > buf.byteLength) throw new ChunkError('field table runs past the end of the chunk');
  const out: FieldEntry[] = [];
  for (let i = 0; i < h.nFields; i++) {
    const o = HEADER_BYTES + i * FIELD_ENTRY_BYTES;
    const dtype = dv.getUint8(o + 2);
    if (!isDtypeCode(dtype)) throw new ChunkError(`field entry ${String(i)}: bad dtype ${String(dtype)}`);
    const e: FieldEntry = {
      id: dv.getUint8(o),
      enc: dv.getUint8(o + 1),
      dtype,
      order: dv.getUint8(o + 3),
      scale: dv.getFloat32(o + 4, true),
      offset: dv.getUint32(o + 8, true),
      length: dv.getUint32(o + 12, true),
      wrap: dv.getUint32(o + 16, true),
    };
    if (e.offset % BLOCK_ALIGN !== 0 || e.offset + e.length > buf.byteLength) {
      throw new ChunkError(`field ${String(e.id)}: bad block ${String(e.offset)}+${String(e.length)}`);
    }
    out.push(e);
  }
  return out;
}

export function allocDecoded(dtype: DtypeCode, len: number): DecodedArray {
  switch (dtype) {
    case 1: return new Uint8Array(len);
    case 2: return new Int8Array(len);
    case 3: return new Uint16Array(len);
    case 4: return new Int16Array(len);
    case 5: return new Uint32Array(len);
    case 6: return new Int32Array(len);
    case 7: return new Float32Array(len);
    case 8: return new Float64Array(len);
  }
}

function bytesPer(dtype: DtypeCode): number {
  return dtype <= 2 ? 1 : dtype <= 4 ? 2 : dtype <= 7 ? 4 : 8;
}

/** RAW: car-major values of dtype (little-endian) → frame-major. */
function decodeRaw(dv: DataView, off: number, e: FieldEntry, n: number, C: number, out: DecodedArray): void {
  const bp = bytesPer(e.dtype);
  if (e.length < n * C * bp) throw new ChunkError(`field ${String(e.id)}: RAW block too short`);
  for (let c = 0; c < C; c++) {
    for (let k = 0; k < n; k++) {
      const p = off + (c * n + k) * bp;
      let v: number;
      switch (e.dtype) {
        case 1: v = dv.getUint8(p); break;
        case 2: v = dv.getInt8(p); break;
        case 3: v = dv.getUint16(p, true); break;
        case 4: v = dv.getInt16(p, true); break;
        case 5: v = dv.getUint32(p, true); break;
        case 6: v = dv.getInt32(p, true); break;
        case 7: v = dv.getFloat32(p, true); break;
        case 8: v = dv.getFloat64(p, true); break;
      }
      out[k * C + c] = v;
    }
  }
}

/** INT_DELTA: i32 seeds + i16 residuals, cumsum `order` times, wrap, × exact scale. */
function decodeIntDelta(
  dv: DataView, off: number, e: FieldEntry, n: number, C: number, scale: number, out: DecodedArray, row: Float64Array,
): void {
  const o = Math.min(e.order, n);
  const need = C * o * 4 + C * (n - o) * 2;
  if (e.length < need) throw new ChunkError(`field ${String(e.id)}: INT_DELTA block too short`);
  const residOff = off + C * o * 4;
  const wrap = e.wrap;
  for (let c = 0; c < C; c++) {
    for (let k = 0; k < o; k++) row[k] = dv.getInt32(off + (c * o + k) * 4, true);
    for (let k = o; k < n; k++) row[k] = dv.getInt16(residOff + (c * (n - o) + (k - o)) * 2, true);
    for (let r = 0; r < e.order; r++) {
      for (let k = 1; k < n; k++) row[k] = (row[k] ?? 0) + (row[k - 1] ?? 0);
    }
    for (let k = 0; k < n; k++) {
      let v = row[k] ?? 0;
      if (wrap) v = ((v % wrap) + wrap) % wrap;
      out[k * C + c] = v * scale;              // f64 product; typed-array store rounds (f32) like numpy astype
    }
  }
}

/** F32_DELTA: 4 byte planes of u32 words, unzigzag (order > 0), cumsum mod 2^32, reinterpret as f32. */
function decodeF32Delta(
  bytes: Uint8Array, off: number, e: FieldEntry, n: number, C: number, scale: number, out: DecodedArray, words: Uint32Array,
): void {
  const N = n * C;
  if (e.length < 4 * N) throw new ChunkError(`field ${String(e.id)}: F32_DELTA block too short`);
  const p0 = off, p1 = off + N, p2 = off + 2 * N, p3 = off + 3 * N;
  for (let w = 0; w < N; w++) {
    let z = ((bytes[p0 + w] ?? 0) | ((bytes[p1 + w] ?? 0) << 8) | ((bytes[p2 + w] ?? 0) << 16) | ((bytes[p3 + w] ?? 0) << 24)) >>> 0;
    if (e.order > 0) z = ((z >>> 1) ^ -(z & 1)) >>> 0;
    words[w] = z;
  }
  for (let r = 0; r < e.order; r++) {
    for (let c = 0; c < C; c++) {
      const b = c * n;
      for (let k = 1; k < n; k++) words[b + k] = ((words[b + k] ?? 0) + (words[b + k - 1] ?? 0)) >>> 0;
    }
  }
  const f = new Float32Array(words.buffer, words.byteOffset, N);
  for (let c = 0; c < C; c++) {
    for (let k = 0; k < n; k++) {
      const v = f[c * n + k] ?? 0;
      out[k * C + c] = scale !== 1 ? v * Math.fround(scale) : v;
    }
  }
}

/**
 * Decode one UCT2 chunk.
 * @param exactScales field id → exact f64 scale (manifest.timeline.fields); replaces the f32 table copy.
 * @param expect optional header/length expectations (throws ChunkError on mismatch).
 */
export function decodeChunk(buf: ArrayBuffer, exactScales?: ReadonlyMap<number, number>, expect?: ChunkExpect): DecodedChunk {
  const header = parseHeader(buf);
  if (header.magic !== UCT2_MAGIC || header.version !== UCT2_VERSION) {
    throw new ChunkError(`bad magic/version ${header.magic} ${String(header.version)}`);
  }
  if (expect) {
    if (expect.bytes !== undefined && buf.byteLength !== expect.bytes) {
      throw new ChunkError(`length ${String(buf.byteLength)} != manifest ${String(expect.bytes)}`);
    }
    if (expect.f0 !== undefined && header.f0 !== expect.f0) throw new ChunkError(`f0 ${String(header.f0)} != ${String(expect.f0)}`);
    if (expect.n !== undefined && header.n !== expect.n) throw new ChunkError(`n ${String(header.n)} != ${String(expect.n)}`);
    if (expect.nCars !== undefined && header.nCars !== expect.nCars) {
      throw new ChunkError(`nCars ${String(header.nCars)} != ${String(expect.nCars)}`);
    }
  }
  const table = parseTable(buf, header);
  const { n, nCars: C } = header;
  const dv = new DataView(buf);
  const bytes = new Uint8Array(buf);
  const row = new Float64Array(n);
  const words = new Uint32Array(n * C);
  const fields = new Map<number, DecodedArray>();
  for (const e of table) {
    const out = allocDecoded(e.dtype, n * C);
    const scale = exactScales?.get(e.id) ?? e.scale;
    switch (e.enc) {
      case ENC_RAW: decodeRaw(dv, e.offset, e, n, C, out); break;
      case ENC_INT_DELTA: decodeIntDelta(dv, e.offset, e, n, C, scale, out, row); break;
      case ENC_F32_DELTA: decodeF32Delta(bytes, e.offset, e, n, C, scale, out, words); break;
      default: throw new ChunkError(`field ${String(e.id)}: unknown encoding ${String(e.enc)}`);
    }
    fields.set(e.id, out);
  }
  return { header, table, fields };
}

/** Typed view of a transferred decoded buffer (inverse of `allocDecoded(...).buffer`). */
export function viewDecoded(dtype: DtypeCode, buffer: ArrayBuffer): DecodedArray {
  switch (dtype) {
    case 1: return new Uint8Array(buffer);
    case 2: return new Int8Array(buffer);
    case 3: return new Uint16Array(buffer);
    case 4: return new Int16Array(buffer);
    case 5: return new Uint32Array(buffer);
    case 6: return new Int32Array(buffer);
    case 7: return new Float32Array(buffer);
    case 8: return new Float64Array(buffer);
  }
}
